import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'

import { MainWrapper, PageWrapper } from 'Common/core'

import SkeletonAgreementGeneral from './SkeletonAgreementGeneral'
import SkeletonCardList from './SkeletonCardList'
import SkeletonDataGrid, {
    SkeletonDataGridFooter,
    SkeletonDataGridToolbar,
} from './SkeletonDataGrid'
import SkeletonEditableRows from './SkeletonEditableRows'
import SkeletonEditor from './SkeletonEditor'
import SkeletonFormGrid from './SkeletonFormGrid'
import SkeletonRuleDetails from './SkeletonRuleDetails'
import SkeletonSearch from './SkeletonSearch'
import SkeletonSection from './SkeletonSection'
import SkeletonSeriesTree from './SkeletonSeriesTree'
import SkeletonSideNav from './SkeletonSideNav'
import SkeletonStackedTables from './SkeletonStackedTables'
import SkeletonTable from './SkeletonTable'
import SkeletonTabs from './SkeletonTabs'
import SkeletonTermsRules from './SkeletonTermsRules'
import SkeletonUpload from './SkeletonUpload'
import SkeletonWebhook from './SkeletonWebhook'

export type PageSkeletonVariant =
    | 'section'
    | 'grid'
    | 'detail'
    | 'tabs'
    | 'editor'
    | 'list'
    | 'profitability'
    | 'series'
    | 'dataGrid'
    | 'agreementGeneral'
    | 'termsRules'
    | 'ruleDetails'
    | 'formGrid'
    | 'cardList'
    | 'upload'
    | 'editableRows'
    | 'webhook'
    | 'stackedTables'

export type PageSkeletonWrapper = 'none' | 'page' | 'main'

export type PageSkeletonProps = {
    variant?: PageSkeletonVariant
    fields?: number
    fieldSpan?: 3 | 4 | 6 | 12
    rows?: number
    columns?: number
    tabs?: number
    navItems?: number
    editors?: number
    fill?: boolean | number | string
    showHeader?: boolean
    wrapper?: PageSkeletonWrapper
    subTabs?: number
    toolbar?: SkeletonDataGridToolbar
    footer?: SkeletonDataGridFooter
    action?: boolean
    works?: number
    advances?: number
    rules?: number
    items?: number
    cardColumns?: 1 | 2
    fieldsPerCard?: number
    badge?: boolean
    infoBar?: boolean
    helpLines?: number
    tableRows?: number
    tableColumns?: number
    toggles?: number
    headerAction?: boolean
    sections?: number
}

export default function PageSkeleton(props: Readonly<PageSkeletonProps>) {
    const {
        variant = 'section',
        fields,
        fieldSpan,
        rows,
        columns,
        tabs,
        navItems,
        editors = 4,
        fill = true,
        showHeader = true,
        wrapper = 'none',
        subTabs,
        toolbar,
        footer,
        action,
        works,
        advances,
        rules,
        items,
        cardColumns,
        fieldsPerCard,
        badge,
        infoBar,
        helpLines,
        tableRows,
        tableColumns,
        toggles,
        headerAction,
        sections,
    } = props

    const content = () => {
        if (variant === 'series') {
            return (
                <Grid container spacing={1} size={12}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <SkeletonSeriesTree fill={fill} />
                    </Grid>
                </Grid>
            )
        }

        if (variant === 'formGrid') {
            return <SkeletonFormGrid fields={fields} rows={rows} columns={columns} />
        }

        if (variant === 'cardList') {
            return (
                <SkeletonCardList
                    items={items}
                    columns={cardColumns}
                    fieldsPerCard={fieldsPerCard}
                    badge={badge}
                />
            )
        }

        if (variant === 'upload') {
            return (
                <SkeletonUpload
                    infoBar={infoBar}
                    fields={fields}
                    helpLines={helpLines}
                    tableRows={tableRows}
                    tableColumns={tableColumns}
                />
            )
        }

        if (variant === 'editableRows') {
            return (
                <SkeletonEditableRows rows={rows} toggles={toggles} headerAction={headerAction} />
            )
        }

        if (variant === 'webhook') {
            return <SkeletonWebhook />
        }

        if (variant === 'stackedTables') {
            return <SkeletonStackedTables sections={sections} rows={rows} columns={columns} />
        }

        if (variant === 'termsRules') {
            return <SkeletonTermsRules rules={rules} />
        }

        if (variant === 'ruleDetails') {
            return <SkeletonRuleDetails />
        }

        if (variant === 'agreementGeneral') {
            return <SkeletonAgreementGeneral works={works} advances={advances} />
        }

        if (variant === 'dataGrid') {
            return (
                <SkeletonDataGrid
                    rows={rows}
                    columns={columns}
                    subTabs={subTabs}
                    toolbar={toolbar}
                    footer={footer}
                    action={action}
                    fill={fill}
                />
            )
        }

        if (variant === 'grid') {
            return (
                <SkeletonSearch
                    rows={rows}
                    columns={columns}
                    fill={fill}
                    showTabs={Boolean(tabs)}
                />
            )
        }

        if (variant === 'list') {
            return (
                <SkeletonSection fill={fill} showHeader={showHeader}>
                    <Grid size={12}>
                        <Skeleton variant="text" width="45%" sx={{ fontSize: '1.25rem', mb: 2 }} />
                        <SkeletonTable
                            rows={rows ?? 8}
                            columns={columns ?? 5}
                            showToolbar={false}
                        />
                    </Grid>
                </SkeletonSection>
            )
        }

        if (variant === 'editor') {
            return (
                <SkeletonSection fill={fill} showHeader={showHeader} footerActions>
                    {Array.from({ length: editors }, (_, index) => (
                        <Grid key={index} size={12}>
                            <SkeletonEditor />
                        </Grid>
                    ))}
                </SkeletonSection>
            )
        }

        if (variant === 'profitability') {
            return (
                <Stack spacing={2} sx={{ width: '100%' }}>
                    <SkeletonSection
                        fields={4}
                        fieldSpan={3}
                        fill={false}
                        showHeader={showHeader}
                    />
                    <Paper sx={{ p: 2 }}>
                        <Skeleton variant="text" width={200} sx={{ fontSize: '1.25rem', mb: 2 }} />
                        <SkeletonTable
                            rows={rows ?? 6}
                            columns={columns ?? 7}
                            showToolbar={false}
                        />
                    </Paper>
                    <Paper sx={{ p: 2 }}>
                        <Skeleton variant="text" width={160} sx={{ fontSize: '1.25rem', mb: 2 }} />
                        <Grid container spacing={4} rowSpacing={2}>
                            {Array.from({ length: 8 }, (_, index) => (
                                <Grid key={index} size={{ xs: 6, md: 3 }}>
                                    <Stack spacing={0.5}>
                                        <Skeleton variant="text" width="60%" />
                                        <Skeleton
                                            variant="text"
                                            width="40%"
                                            sx={{ fontSize: '1.5rem' }}
                                        />
                                    </Stack>
                                </Grid>
                            ))}
                        </Grid>
                    </Paper>
                </Stack>
            )
        }

        if (variant === 'tabs') {
            return (
                <Stack spacing={2} sx={{ width: '100%' }}>
                    <SkeletonTabs tabs={tabs} />
                    <SkeletonSection
                        fields={fields}
                        fieldSpan={fieldSpan}
                        fill={fill}
                        showHeader={showHeader}
                    />
                </Stack>
            )
        }

        if (variant === 'detail') {
            return (
                <Stack direction="row" spacing={0} sx={{ width: '100%' }}>
                    <SkeletonSideNav items={navItems} />
                    <Box sx={{ flexGrow: 1, minWidth: 0, px: 3, py: 2 }}>
                        <SkeletonSection
                            fields={fields}
                            fieldSpan={fieldSpan}
                            fill={fill}
                            showHeader={showHeader}
                        />
                    </Box>
                </Stack>
            )
        }

        return (
            <SkeletonSection
                fields={fields}
                fieldSpan={fieldSpan}
                fill={fill}
                showHeader={showHeader}
                footerActions
            />
        )
    }

    if (wrapper === 'page') return <PageWrapper>{content()}</PageWrapper>

    if (wrapper === 'main') {
        return (
            <MainWrapper>
                <PageWrapper>{content()}</PageWrapper>
            </MainWrapper>
        )
    }

    return content()
}
